import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Upload, Button, Alert, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { incidentService } from "@iso/services";
import { apiEndpoint } from "@iso/lib/helpers/endpoint";
import { authHeader } from "@iso/lib/helpers/authHeader";

const { TextArea } = Input;

const CompleteActionModal = ({ visible, action, onCancel, onSaved }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [attachmentIds, setAttachmentIds] = useState([]);
  const [fileList, setFileList] = useState([]);

  const evidenceRequired = action?.evidence_required;

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setAttachmentIds([]);
      setFileList([]);
    }
  }, [visible, form]);

  const handleUploadChange = ({ file, fileList: newFileList }) => {
    if (file.status === "done" && file.response?.attachment?.id) {
      setAttachmentIds((prev) => [...prev, file.response.attachment.id]);
    }
    if (file.status === "removed") {
      const removedId = file.response?.attachment?.id ?? file.uid;
      setAttachmentIds((prev) => prev.filter((id) => id !== removedId));
    }
    setFileList(newFileList);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // If evidence required, must have text or at least one attachment
      if (evidenceRequired && !values.evidence && attachmentIds.length === 0) {
        message.error("Evidence is required — please enter a description or upload a file.");
        return;
      }

      setLoading(true);
      await incidentService.completeCorrectiveAction(
        action.id,
        values.evidence || null,
        attachmentIds
      );
      message.success("Corrective action marked as complete.");
      onSaved && onSaved();
      onCancel();
    } catch (err) {
      if (err.errorFields) return;
      message.error(
        err?.response?.data?.message || "Failed to complete corrective action."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={visible}
      title="Mark as Complete"
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      destroyOnClose
      okText="Mark Complete"
      okButtonProps={{ style: { backgroundColor: "#0B2A1B", borderColor: "#0B2A1B" } }}
    >
      {action && (
        <p style={{ color: "#667085", marginBottom: 16, fontSize: 13 }}>
          <strong>Action:</strong> {action.description}
        </p>
      )}

      {evidenceRequired && (
        <Alert
          type="warning"
          message="Evidence required — provide a description and/or attach files."
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      <Form form={form} layout="vertical">
        <Form.Item
          name="evidence"
          label="Evidence Description"
          rules={
            evidenceRequired && attachmentIds.length === 0
              ? [{ required: true, whitespace: true, message: "Please describe the evidence or upload a file" }]
              : []
          }
        >
          <TextArea
            rows={3}
            placeholder={
              evidenceRequired
                ? "Describe the evidence for completing this action..."
                : "Optional notes on completion..."
            }
            maxLength={1000}
            showCount
          />
        </Form.Item>

        <Form.Item label={evidenceRequired ? "Evidence Files" : "Attachments (optional)"}>
          <Upload
            action={apiEndpoint.attachments}
            headers={authHeader(true)}
            listType="picture"
            name="Attachment[file]"
            fileList={fileList}
            multiple
            onChange={handleUploadChange}
            data={{ type: "corrective_action", storage: "filesystem" }}
          >
            <Button icon={<UploadOutlined />}>Upload File</Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CompleteActionModal;
